import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState
} from 'react';
import router from 'next/router';

import { CheckoutProviderT } from './types';
import useTiers from './hooks/useTiers';
import { useViewEvent } from 'features/events/context/ViewEvent';
import useTickets from './hooks/useTickets';
import useMultiTierCheckoutSession from './hooks/useMultiTierCheckoutSession';
import {
  getDiscountCodeByCode,
  useGetSuggestedFriendsFromEvent,
  useValidateEventUrlCommand
} from 'legacy/events/hooks';
import useSpecialCode from './hooks/useSpecialCode';
import { useGetMe } from 'legacy/user/hooks';
import usePaymentMethod from 'hooks/usePaymentMethod';
import { Card } from 'legacy/common/types/models';
import useWaivers from './hooks/useWaivers';
import useRequestScreening from './hooks/useRequestScreening';
import {
  CheckoutSteps,
  LegacyCheckoutUpdateDataToSend,
  MultiTierCheckoutSession,
  QuantityChangeEvents
} from '../types';
import { LoginResponse } from 'legacy/auth/types';
import useCheckoutTimer from './hooks/useCheckoutTimer';
import useAddonTiers from './hooks/useAddonTiers';

// NOTE OLD CODE / HACK
// This seems to be always be enough time, in the off chance it is not then the user will be prompted to restart the checkout session.
const WAIT_TIME_FOR_NEWLY_AUTHENTICATED_USER_MILLISECONDS = 2000;

const CheckoutContext = createContext({} as CheckoutProviderT);

function CheckoutProvider({
  children,
  goToStep,
  currentStep
}: PropsWithChildren<{
  goToStep: (step: string) => void;
  currentStep: string;
}>) {
  const [isAuthenticatingUser, setIsAuthenticatingUser] = useState(false);
  const [eventShareToken, setEventShareToken] = useState<string | null>(null);
  const {
    event,
    metaPixelRef,
    discountCodeData: defaultDiscountCode,
    unlockedTier,
    promoterCode: defaultPromoterCode,
    refetchEvent
  } = useViewEvent();
  const { data: user, isLoading: isLoadingUser } = useGetMe();
  const validateEventUrlCommand = useValidateEventUrlCommand();

  // Default discount code is applied inside a useEffect below as it is dependent on checkout session update mutation.
  const specialCode = useSpecialCode();
  const discountData = getDiscountCodeByCode(
    event._id,
    specialCode.discountCode.value
  );

  const checkoutTimer = useCheckoutTimer();
  const ticketTiers = useTiers(event);
  const addonTiers = useAddonTiers(event);
  const hasTierNav =
    currentStep === CheckoutSteps.TicketTiers
      ? ticketTiers.menuSections.length > 0
      : addonTiers.menuSections.length > 0;
  const checkoutSession = useMultiTierCheckoutSession(
    event,
    discountData.data,
    ticketTiers.data,
    checkoutTimer.isExpired
  );
  const tickets = useTickets(event);
  const waivers = useWaivers(event);
  const paymentMethod = usePaymentMethod({
    returnUrl: window.location.href
  });
  const requestScreening = useRequestScreening(
    event._id,
    tickets.tierRequiringApproval,
    ticketTiers.data
  );

  const isLoading = Boolean(
    checkoutSession.isStartingCheckout ||
      checkoutSession.isUpdatingCheckout ||
      ticketTiers.isLoadingGetTierFromShareToken ||
      isAuthenticatingUser ||
      isLoadingUser
  );

  // Prefetch Suggested Friends during checkout
  // TODO: Do we need this?
  useGetSuggestedFriendsFromEvent(event._id);

  /**
   * Handles the success of authentication for signed otu users trying to make a transaction.
   * Hack for simple sign on to wait for any resources on the newly authenticated user's Stripe customer account to be created,
   * and then to update the active checkout session with the user.
   */
  const onAuthenticationSuccess = (loginResponse: LoginResponse) => {
    setIsAuthenticatingUser(true);

    if (eventShareToken) {
      validateEventUrlCommand.mutate({
        eventId: event._id,
        shareToken: eventShareToken
      });
    }

    if (!tickets.tierRequiringApproval) {
      setTimeout(() => {
        if (loginResponse.user.defaultCard) {
          paymentMethod.selectedCard.setSelectedCard(
            loginResponse.user.defaultCard
          );
        }

        checkoutSessionUpdater(
          {
            card: loginResponse.user.defaultCard ?? null
          },
          loginResponse.user.cards
        )
          .then(() => {
            setIsAuthenticatingUser(false);
          })
          .catch(() => {
            setIsAuthenticatingUser(false);
          });
      }, WAIT_TIME_FOR_NEWLY_AUTHENTICATED_USER_MILLISECONDS);
    } else {
      setIsAuthenticatingUser(false);
    }
  };

  /**
   * Gathers checkout update data.
   * This allows to retrieve the data from the different hooks to be sent to the API.
   * This also allows us to easily call the checkout update and only update the actual state on success.
   */
  const checkoutSessionUpdater = (
    updateData: Partial<LegacyCheckoutUpdateDataToSend>,
    cards?: Card[]
  ): ReturnType<typeof checkoutSession.updateCheckout> => {
    const checkoutUpdateData: LegacyCheckoutUpdateDataToSend = {
      card:
        updateData.card ||
        paymentMethod.selectedCard.value ||
        (checkoutSession.checkoutSession as MultiTierCheckoutSession).card,
      promoterCode: updateData.discountCode || discountData?.data?.code,
      discountCode:
        updateData.discountCodeType === 'discountCode' ||
        (discountData?.data?.discount &&
          updateData.discountCodeType !== 'promoterAttribution')
          ? updateData.discountCode || discountData?.data.code
          : undefined,
      ticketTiers: updateData.ticketTiers || tickets.selectedTicketTiers,
      privateTierCodes:
        updateData.privateTierCodes || ticketTiers.privateTierCodes,
      stripePaymentMethodType: updateData.stripePaymentMethodType || undefined
    };

    return checkoutSession.updateCheckout(checkoutUpdateData, cards);
  };

  /**
   * Applies a discount code and updates the checkout session.
   */
  const applyDiscountCodeAndSave = (
    code: string,
    discountCodeType?: string
  ) => {
    specialCode.discountCode.applyDiscountCode(code, (discountCode) =>
      checkoutSessionUpdater({ discountCode, discountCodeType })
    );
  };

  /**
   * Toggles the ticket tier and updates the checkout session.
   */
  const toggleTicketTierAndSave = (
    tierId: string,
    unlockCode?: string,
    discountCode?: string
  ) => {
    tickets.toggleTicketTier(tierId, (newTickets) => {
      const updateData = {
        ticketTiers: newTickets,
        privateTierCodes: null,
        discountCode
      };

      // When unlocking a private tier via a code, we need to pass all unlock codes the user has entered to the API.
      // This allows us to support unlocking multiple private tiers.
      // But at this stage, the private tier codes state has not necessarily yet been updated to include the new unlock code,
      // so we are adding it manually here in order to send the optimistic update to the API.
      if (unlockCode) {
        updateData.privateTierCodes = {
          ...ticketTiers.privateTierCodes,
          [tierId]: unlockCode
        };
      }

      return checkoutSessionUpdater(updateData);
    });
  };

  /**
   * Updates the quantity of a ticket tier and updates the checkout session.
   */
  const updateTicketTierQuantityAndSave = (
    ticketId: string,
    event: QuantityChangeEvents
  ) => {
    tickets.updateQuantity(ticketId, event, (newTickets) =>
      checkoutSessionUpdater({ ticketTiers: newTickets })
    );
  };

  /**
   * Adds a new card to the user's account and updates the checkout session.
   */
  const addNewCardAndSave = (options?: { onSuccess: () => void }) =>
    paymentMethod.addNewCard({
      onSuccess: (newCard: string, cards: Card[]) =>
        checkoutSessionUpdater({ card: newCard }, cards).finally(() => {
          options?.onSuccess?.();
        })
    });

  /**
   * Sets a card to be used for the checkout session and updates the checkout session.
   */
  const selectCardAndSave = (
    selectedCardId: string,
    options?: { onSuccess: () => void }
  ) =>
    paymentMethod.selectedCard.setSelectedCard(selectedCardId, (cardId) =>
      checkoutSessionUpdater({ card: cardId }).finally(() => {
        options?.onSuccess?.();
      })
    );

  /**
   * Sets a card to be used for the checkout session and updates the checkout session.
   */
  const selectBuyNowPayLaterAndSave = (
    selectedBuyNowPayLater: 'klarna' | 'affirm' | null,
    options?: { onSuccess: () => void }
  ) =>
    paymentMethod.selectedBuyNowPayLater.setSelectedBuyNowPayLater(
      selectedBuyNowPayLater,
      (buyNowPayLater) =>
        checkoutSessionUpdater({
          stripePaymentMethodType: buyNowPayLater
        }).finally(() => {
          options?.onSuccess?.();
        })
    );

  /**
   * Wraps the onSuccess with Meta Pixel tracking
   */
  const handleCheckoutSuccess = () => {
    if (metaPixelRef?.current) {
      metaPixelRef.current.trackCheckoutSuccess({
        eventId: event._id,
        eventName: event.title,
        value: checkoutSession.checkoutSession?.total || 0,
        numberOfTickets: Object.values(
          checkoutSession.checkoutSession?.ticketBasket || {}
        ).reduce((acc, curr) => acc + curr, 0),
        currency: checkoutSession.checkoutSession?.currency || 'USD'
      });
    }

    // Refetch the event to get the latest data on guests
    refetchEvent();
  };

  /**
   * Executes when a checkout session is successfully created.
   * This function applies an initial discount code, credit card and sets the unlocked tier as the initially selected tier (if tier exists on the event).
   */
  useEffect(() => {
    if (
      checkoutSession.checkoutSession &&
      !checkoutSession.isStartingCheckout &&
      !ticketTiers.isLoadingProducts &&
      !ticketTiers.isLoadingUnauthenticatedProducts
    ) {
      if (user?.defaultCard) {
        paymentMethod.selectedCard.setSelectedCard(user.defaultCard);
      }

      if ((defaultPromoterCode || defaultDiscountCode) && !unlockedTier) {
        specialCode.discountCode.applyDiscountCode(
          defaultPromoterCode || defaultDiscountCode.code
        );
      } else if (unlockedTier) {
        const tierToBeUnlocked = ticketTiers.mappedProductsToTiers?.find(
          (tier) => tier._id === unlockedTier.id
        );

        // Unlocked tier codes from outside the checkout flow have been checked
        // But the tier itself has not yet been checked, we need to make sure we can safely add it to the checkout session.
        // We are also ignoring the discount code here, as we can't have discount codes for private tiers.
        if (tierToBeUnlocked && !tierToBeUnlocked.isDisabled) {
          ticketTiers.setPrivateTierCode(
            unlockedTier.id,
            unlockedTier.shareToken
          );

          if (!ticketTiers.isTierDisabled(unlockedTier.id)) {
            if (ticketTiers.isTierRequiringApproval(unlockedTier.id)) {
              tickets.toggleTierRequiringApproval(unlockedTier.id);
            } else {
              toggleTicketTierAndSave(unlockedTier.id, unlockedTier.shareToken);
            }
          }
        }
      }
    }
  }, [
    checkoutSession.isStartingCheckout,
    ticketTiers.mappedProductsToTiers,
    ticketTiers.isLoadingProducts,
    ticketTiers.isLoadingUnauthenticatedProducts
  ]);

  /**
   * Clears any selected tickets that become invalid when a discount code with tier restrictions is applied.
   */
  useEffect(() => {
    const appliedDiscountData = discountData?.data;

    if (
      !appliedDiscountData?.tierIds ||
      appliedDiscountData.tierIds.length === 0
    ) {
      return;
    }

    const currentSelectedTiers = Object.keys(tickets.selectedTicketTiers);
    const invalidTiers = currentSelectedTiers.filter(
      (tierId) => !appliedDiscountData.tierIds.includes(tierId)
    );

    if (invalidTiers.length > 0) {
      const validTickets = Object.keys(tickets.selectedTicketTiers)
        .filter((tierId) => appliedDiscountData.tierIds.includes(tierId))
        .reduce((acc, tierId) => {
          acc[tierId] = tickets.selectedTicketTiers[tierId];
          return acc;
        }, {} as typeof tickets.selectedTicketTiers);

      checkoutSessionUpdater({ ticketTiers: validTickets });
    }
  }, [discountData?.data?.tierIds, discountData?.data?.code]);

  /**
   * Starts a checkout session
   */
  useEffect(() => {
    if (!event?._id || checkoutTimer.isExpired) {
      return;
    }

    checkoutSession.startCheckout(event._id);
    tickets.reset();
    waivers.reset();
    goToStep(CheckoutSteps.TicketTiers);

    // Track checkout start event with Meta Pixel
    if (metaPixelRef?.current) {
      metaPixelRef.current.trackCheckoutInitiated({
        eventId: event._id,
        eventName: event.title
      });
    }
  }, [event?._id, checkoutTimer.isExpired]);

  // Not a huge fan of this implementation.
  // When a user selects a tier for approval while logged out, we need to make sure they still have access to that tier after they logged in.
  useEffect(() => {
    if (tickets.tierRequiringApproval && user) {
      if (ticketTiers.isTierDisabled(tickets.tierRequiringApproval)) {
        tickets.toggleTierRequiringApproval(tickets.tierRequiringApproval);
      }
    }
  }, [user, ticketTiers.data]);

  /**
   * NOTE: VERY OLD CODE.
   * TODO: Find if this is actually useful or patching a bug somewhere.
   *
   * This ensures that checkout sessions which begin with an unauthenticated user for a private event do
   * not have the checkout modal dismissed following authentication. Without this the rendering logic for
   * the event screen will render an entirely different component instead (`EventInvite`), thus clearing
   * the active checkout session for the user.
   */
  useEffect(() => {
    const { invite } = router.query;

    if (invite && !user) {
      setEventShareToken(invite as string);
      delete router.query.invite;
      router.push(router);
    }
  }, []);

  return (
    <CheckoutContext.Provider
      value={{
        checkoutTimer,
        ticketTiers,
        addonTiers,
        isLoading,
        checkoutSession,
        specialCode,
        tickets,
        requestScreening,
        waivers,
        user,
        hasTierNav,
        paymentMethod,
        appliedDiscount: {
          data: discountData.data,
          isLoading: discountData.isLoading
        },
        toggleTicketTierAndSave,
        applyDiscountCodeAndSave,
        updateTicketTierQuantityAndSave,
        addNewCardAndSave,
        selectCardAndSave,
        selectBuyNowPayLaterAndSave,
        onSuccess: handleCheckoutSuccess,
        onAuthenticationSuccess,
        goToStep,
        currentStep
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
}

export const useCheckout = () => useContext(CheckoutContext);

export default CheckoutProvider;
